import { availabilityEndsAt, isAvailableAt } from "@/lib/is-available-at"
import type { SchedulingSlot } from "@/lib/scheduling/types"
import type { Availability, Cart, Category } from "@/types"

/** A bag line that shortens the offered grid, with the hour it stops at. */
export type SlotRestriction = {
  name: string
  /** "HH:mm" the line stops being available on the slots' day; null when unknown. */
  endsAt: string | null
}

export type FilteredSlots = {
  slots: SchedulingSlot[]
  restrictions: SlotRestriction[]
}

type AvailabilityHolder = { availabilityId?: string[] }

function categoryOf(categories: Category[], offerId: string): Category | undefined {
  return categories.find((category) => (category.itemOfferId ?? []).includes(offerId))
}

function lineWindows(cart: Cart, categories: Category[]): Array<{
  name: string
  sources: AvailabilityHolder[]
}> {
  return cart.items.map((cartItem) => {
    const category = categoryOf(categories, cartItem.itemOffer.id)
    const sources: AvailabilityHolder[] = [cartItem.itemOffer]
    if (category) sources.push(category)
    return { name: cartItem.item.name, sources }
  })
}

function holds(sources: AvailabilityHolder[], availabilities: Availability[], when: Date): boolean {
  return sources.every((source) => isAvailableAt(source.availabilityId, availabilities, when))
}

/**
 * Hides the slots at which some bag line is not available, using the
 * availability windows already in the catalog payload (UC5, answer 27).
 *
 * Display only: the checkout is the authority and still refuses a `readyAt`
 * the bag cannot honour. A bag whose lines never intersect keeps every slot
 * hidden and reports which line is responsible.
 */
export function filterSlotsForCart(
  slots: SchedulingSlot[],
  cart: Cart,
  availabilities: Availability[],
  categories: Category[],
): FilteredSlots {
  if (slots.length === 0) return { slots: [], restrictions: [] }

  const lines = lineWindows(cart, categories)
  if (lines.length === 0) return { slots, restrictions: [] }

  const kept: SchedulingSlot[] = []
  const restrictedNames = new Set<string>()

  for (const slot of slots) {
    const at = new Date(slot.readyAt)
    if (Number.isNaN(at.getTime())) continue

    const blocking = lines.filter((line) => !holds(line.sources, availabilities, at))
    if (blocking.length === 0) {
      kept.push(slot)
      continue
    }
    for (const line of blocking) restrictedNames.add(line.name)
  }

  const reference = new Date(slots[0].readyAt)
  const restrictions: SlotRestriction[] = [...restrictedNames].map((name) => {
    const line = lines.find((candidate) => candidate.name === name)
    const ends = line
      ? line.sources
          .map((source) => availabilityEndsAt(source.availabilityId, availabilities, reference))
          .filter((value): value is string => value !== null)
          .sort()[0] ?? null
      : null
    return { name, endsAt: ends }
  })

  return { slots: kept, restrictions }
}

/** Why a merchant-offered slot is not listed (item availability cutoff). */
export function formatSlotRestriction(restriction: SlotRestriction): string {
  if (!restriction.endsAt) return `${restriction.name} não está disponível em alguns horários`
  return `${restriction.name} só até ${restriction.endsAt}`
}
